import * as React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { axisClasses } from "@mui/x-charts";
import { Box } from "@mui/material";
import DatePicker from "../../Atoms/DatePicker/DatePicker";
import moment from "moment";
import SelectDropdown from "../../Atoms/Select/Select";
import { useDispatch, useSelector } from "react-redux";
import { getDashboardGraph } from "../../../screens/dashboard-screen/DashboardSaga";
import { componentKey } from "../../../screens/dashboard-screen/DashboardSlice";

export default function Bar() {
  const { dashboardGraphData } = useSelector(
    (state: any) => state[componentKey]
  );
  const dispatch = useDispatch();

  const [filterType, setFilterType] = React.useState("currentWeek");
  const [customRange, setCustomRange] = React.useState({
    startDate: null,
    endDate: null,
  });

  const getCustomFilterData = (filterType: string) => {
    switch (filterType) {
      case "currentWeek":
        return "Current Week";
      case "lastWeek":
        return "Last Week";
      case "custom":
        return customRange?.startDate && customRange?.endDate
          ? "From " +
              moment(customRange?.startDate).format("MM/DD/YYYY") +
              " To " +
              moment(customRange?.endDate).format("MM/DD/YYYY")
          : "All";
      default:
        return "";
    }
  };

  const initialDataset = [
    {
      patient: dashboardGraphData?.patient,
      facility: dashboardGraphData?.facility,
      provider: dashboardGraphData?.provider,
      staff: dashboardGraphData?.staff,

      month: getCustomFilterData(filterType), // Full month name (e.g., February)
      date: new Date(),
    },
  ];

  // Function to filter dataset
  const chartSetting = {
    yAxis: [
      {
        label: "Total",
      },
    ],
    // No fixed width: MUI X Charts sizes to its container when `width` is
    // omitted. A hard pixel width rendered an SVG that couldn't shrink, so the
    // chart overflowed its card on iPad (and both orientations mapped to the
    // same "small-tablet" breakpoint regardless of actual space).
    height: 300,
    sx: {
      [`.${axisClasses.left} .${axisClasses.label}`]: {
        transform: "translate(-10px, 0)",
      },
    },
  };

  React.useEffect(() => {
    const payload = {
      key: filterType !== "custom" ? filterType : undefined,
      // Send UTC ISO instants anchored to the user's local day so the backend
      // filters the correct calendar day regardless of server timezone.
      startDate: customRange?.startDate
        ? moment(customRange?.startDate).startOf("day").toISOString()
        : undefined,
      endDate: customRange?.endDate
        ? moment(customRange?.endDate).endOf("day").toISOString()
        : undefined,
    };
    dispatch(getDashboardGraph(payload));
  }, [filterType, customRange?.startDate, customRange?.endDate]);

  return (
    // Explicit full width: the chart now sizes to its container, and this Box
    // is a flex item in a `justify-center` parent — without a definite width it
    // would size to its content and the chart would collapse.
    <Box sx={{ width: "100%" }}>
      <div className="">
        <div className="mb-4 flex flex-wrap gap-3 justify-between items-center">
          {" "}
          <div className="text-sm	font-semibold	 text-[#565656]">Graph </div>
          <Box
            display="flex"
            alignItems="end"
            justifyContent={"end"}
            gap={2}
            // marginBottom={2}
          >
            <div className="flex justify-end  items-center gap-3">
              <SelectDropdown
                label=""
                name="status"
                value={filterType}
                options={[
                  { label: "Current Week", value: "currentWeek" },
                  { label: "Last Week", value: "lastWeek" },
                  { label: "Custom", value: "custom" },
                ]}
                onChangeCb={(e) => {
                  setFilterType(e.target.value);
                  if (e.target.value !== "custom") {
                    setCustomRange((prevData: any) => ({
                      ...prevData,
                      startDate: null,
                      endDate: null,
                    }));
                  }
                }}
              />
              {filterType === "custom" && (
                <>
                  <DatePicker
                    name={"Start Date"}
                    value={customRange.startDate}
                    onChangeCb={(value) => {
                      setCustomRange((prevData: any) => ({
                        ...prevData,
                        startDate: value,
                      }));
                    }}
                    label="Start Date"
                    style={{ width: "100%" }}
                    maxDate={moment()}
                  />
                  <DatePicker
                    name={"End Date"}
                    value={customRange.endDate}
                    onChangeCb={(value) => {
                      setCustomRange((prevData: any) => ({
                        ...prevData,
                        endDate: value,
                      }));
                    }}
                    label="Due Date"
                    style={{ width: "100%" }}
                    maxDate={moment()}
                  />
                </>
              )}
            </div>
          </Box>
        </div>
      </div>

      <BarChart
        dataset={initialDataset}
        xAxis={[{ scaleType: "band", dataKey: "month" }]}
        series={[
          { dataKey: "patient", label: "Patient", color: "#9fbedb" },
          { dataKey: "facility", label: "Facility", color: "#a1d7a1" },
          { dataKey: "provider", label: "Provider", color: "#ffd0bf" },
          { dataKey: "staff", label: "Staff", color: "#f573c3" },
        ]}
        {...chartSetting}
      />
    </Box>
  );
}
